import { LOGO, PROPOSAL_SAMPLE as P, waLink, WA_MSG } from "../../data/content";
import { Chapter, Reveal, Btn } from "../shared";
import { WhatsAppIcon } from "../Navbar";

const Ornament = ({ light = false }) => (
  <div className="flex items-center justify-center gap-3">
    <span className={`h-px w-14 ${light ? "bg-gold/60" : "bg-gold/50"}`} />
    <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
    <span className={`h-px w-14 ${light ? "bg-gold/60" : "bg-gold/50"}`} />
  </div>
);

export default function SampleProposal() {
  return (
    <section className="relative overflow-hidden bg-emerald py-24 md:py-32" data-testid="sample-proposal">
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#E8D3A2 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
      <div className="relative mx-auto max-w-5xl px-6 md:px-12">
        <div className="text-center">
          <Chapter num="05" title="Sample Wedding Proposal" dark center />
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl text-ivory leading-[1.05]">
              Your wedding, <span className="italic text-gold-light">beautifully planned</span>
            </h2>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative mt-14 bg-ivory p-6 shadow-[0_40px_80px_rgba(0,0,0,0.35)] sm:p-10 md:p-14" data-testid="proposal-sheet">
            <div className="pointer-events-none absolute inset-3 border border-gold/40" />
            <div className="pointer-events-none absolute inset-4 border border-gold/20" />

            <div className="relative flex flex-col items-center text-center">
              <img src={LOGO} alt="The Big Wedd emblem" className="h-14 w-14 rounded-full object-cover ring-1 ring-gold/40" />
              <p className="mt-4 text-[10px] uppercase tracking-[0.4em] text-gold-text">The Big Wedd · Wedding Proposal</p>

              <h3 className="mt-6 font-serif text-5xl md:text-7xl leading-none text-emerald italic" data-testid="proposal-couple">{P.couple}</h3>
              <p className="mt-5 font-serif text-2xl md:text-4xl text-gold-text" data-testid="proposal-date">{P.date}</p>

              <div className="mt-6">
                <Ornament />
              </div>

              <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-stone2">
                {P.destination} <span className="mx-2 text-gold">◆</span> {P.guests}
              </p>
              <p className="mt-3 inline-flex rounded-full bg-emerald px-6 py-2 text-[10px] uppercase tracking-[0.3em] text-gold-light" data-testid="proposal-pack">
                {P.pack}
              </p>
            </div>

            <div className="relative mt-10 space-y-5" data-testid="proposal-events">
              {P.events.map((ev, i) => (
                <div key={ev.name} className="border border-gold/25 bg-white/70 p-5 md:p-6" data-testid={`proposal-event-${i}`}>
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="font-serif text-2xl md:text-3xl text-emerald italic">{ev.name}</p>
                      <p className="mt-2 text-[9px] uppercase tracking-[0.25em] text-stone2/70">Optional Add-Ons — nothing compulsory</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {ev.addons.map((a) => (
                          <span key={a} className="rounded-full border border-gold/40 px-3 py-1 text-[10px] uppercase tracking-[0.12em] text-gold-text">+ {a}</span>
                        ))}
                      </div>
                    </div>
                    <div className="grid flex-1 grid-cols-3 gap-3 md:max-w-md md:gap-5">
                      {[
                        ["Accommodation & Stays", ev.budget.stays],
                        ["Decor", ev.budget.decor],
                        ["Artist", ev.budget.artist],
                      ].map(([label, val]) => (
                        <div key={label} className="text-center md:text-right" data-testid={`proposal-event-${i}-budget`}>
                          <p className="text-[8px] md:text-[9px] uppercase tracking-[0.18em] text-stone2/70">{label}</p>
                          <p className="mt-1 font-serif text-base md:text-lg text-emerald">{val}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-gold/20 pt-3">
                    <span className="text-[9px] uppercase tracking-[0.25em] text-stone2/70">{ev.name} Budget</span>
                    <span className="font-serif text-lg md:text-xl text-gold-text">{ev.total}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative mt-8 bg-emerald p-6 md:p-8" data-testid="proposal-grand-total">
              <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
                <div className="flex items-center gap-4">
                  <span className="inline-block h-2 w-2 rotate-45 bg-gold" />
                  <span className="text-[11px] uppercase tracking-[0.3em] text-gold-light">Grand Total — Illustrative</span>
                </div>
                <p className="font-serif text-4xl md:text-5xl text-ivory">{P.grandTotal}</p>
              </div>
            </div>

            <div className="relative mt-8">
              <Ornament />
              <p className="mt-5 text-center font-serif text-lg md:text-xl italic text-stone2">
                Every proposal is personalised — your events, add-ons and budgets are built around your story.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-12 text-center">
          <Btn to="/enquire" testid="proposal-cta">Request Your Wedding Proposal</Btn>
          <a href={waLink(WA_MSG)} target="_blank" rel="noopener noreferrer" data-testid="proposal-cta-whatsapp" className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ivory/60 hover:text-gold-light">
            <WhatsAppIcon className="h-4 w-4" /> or chat on WhatsApp
          </a>
        </Reveal>
      </div>
    </section>
  );
}
